import {
  K8sKind,
  type K8sClient,
  type K8sObject
} from 'types/api/kubernetes/kubernetes'
import { naturalSortBy } from 'utils/list'

// Custom, one off colors for graph "selected" colors of various types of objects
export enum KubernetesKindColors {
  DEPLOYMENT = '#6E40AA',
  DEPLOYMENTCONFIG = '#AE90D5',
  DAEMONSET = '#1AC7C2',
  REPLICATIONCONTROLLER = '#7DE8BF',
  ROLLOUT = '#0E6655',
  REPLICASET = '#23C386',
  STATEFULSET = '#407DE0',
  POD = '#ADF37B'
}

export interface TargetsByOwner {
  target: K8sObject
  name: string | undefined
  uid: string
  children: Record<string, K8sObject[]>
}

type TargetParent = {
  [ownerReferences: string]: K8sObject[]
}

export const flattenKubernetesTargets = (targets: K8sClient[] = []) => {
  return targets.flatMap((cluster) => cluster?.objects ?? [])
}

export const flattenKubernetesTargetsWithOwnerId = (targets = []) => {
  return targets.flatMap(
    (cluster) =>
      (cluster?.objects ?? []).map((childObject) => ({
        owningTeamId: cluster?.teamId,
        ...childObject
      })) ?? []
  )
}

export const isK8TargetSelected = (target, selectedObjects = new Set()) => {
  return selectedObjects.has(target.uid)
}

export const getTotalSelectedK8Clients = (selectedObjects) =>
  selectedObjects.size

export function getAllSelectedK8PodObjects(
  selectedObjects = new Set(),
  targetsByOwners: TargetsByOwner[] = []
) {
  const selected: K8sObject[] = []

  if (selectedObjects.size < 1 || targetsByOwners.length < 1) {
    return selected
  }

  return targetsByOwners.reduce((acc, owner) => {
    // parent-less POD
    if (selectedObjects.has(owner.uid)) {
      // parent-less POD
      if (owner.target.kind === 'POD') {
        acc.push(owner.target)
      }

      // add any pods this object contains
      if (owner.children.POD) {
        acc = acc.concat(owner.children.POD)
      }
    } else if (owner.children.POD) {
      // add any selected pods this object contains if the parent is NOT selected
      owner.children.POD.forEach((child) => {
        if (selectedObjects.has(child.uid)) {
          acc.push(child)
        }

        if (selectedObjects.has(child.ownerReferences)) {
          acc.push(child)
        }
      })
    }

    return acc
  }, selected)
}

export function getTotalSelectedK8Pods(
  selectedObjects = new Set(),
  targetsByOwners: TargetsByOwner[] = []
) {
  return getAllSelectedK8PodObjects(selectedObjects, targetsByOwners).length
}

// FixMe: to be used when reducing the shown tags to only those available after an initial choice
// function isOwnerSelected(target, selectedTargetParams) {
//   const { ownerReferences = '' } = target
//
//   return Object.values(selectedTargetParams).includes(ownerReferences)
// }

export const formatKubernetesParams = (
  mappedTargetList /*selectedTargetParams*/
) => {
  // const emptyTargetParams = Object.values(selectedTargetParams).length === 0
  const out = naturalSortBy(mappedTargetList, (t) => t.name).reduce(
    ({ keyValueSets, labelKeys }, parent) => {
      // FixMe: to be used when reducing the shown tags to only those available after an initial choice
      // if (!emptyTargetParams) {
      //   if (!isOwnerSelected(target, selectedTargetParams)) {
      //     return { keyValueSets, labelKeys }
      //   }
      // }

      const { uid, kind, name } = parent.target
      const targetName = name || uid

      if (!labelKeys[kind]) {
        labelKeys[kind] = []
      }

      //add key-value to the set
      if (kind) {
        keyValueSets.add(`${K8sKind[kind] ?? kind}:${targetName}`)
        labelKeys[kind].push(parent)
      }

      return { keyValueSets, labelKeys }
    },
    {
      keyValueSets: new Set(),
      labelKeys: {}
    }
  )

  return {
    availableTargetParams: out.keyValueSets,
    labelKeys: Object.fromEntries(
      Object.entries(out.labelKeys).map(([kind, targets]) => [kind, targets])
    )
  }
}

export const sortKubernetesObjectParams = () => {
  const defaults = [
    'DEPLOYMENT',
    'DEPLOYMENTCONFIG',
    'DAEMONSET',
    'REPLICASET',
    'REPLICATIONCONTROLLER',
    'STATEFULSET',
    'POD'
  ]
  const displayed = defaults
  return { displayed, others: [] }
}

export const getListByK8Property = (targets, propertyName) => {
  return targets.reduce((accum, target) => {
    if (target[propertyName]) accum.add(target[propertyName])
    return accum
  }, new Set())
}

export function groupKubernetesObjects(targets: K8sObject[]) {
  return targets.reduce((parents, target) => {
    const { ownerReferences } = target

    if (ownerReferences) {
      if (!parents[ownerReferences]) {
        parents[ownerReferences] = []
      }

      parents[ownerReferences]?.push(target)
    }

    return parents
  }, {} as TargetParent)
}

export function getTargetAndDescendants(
  target: K8sObject,
  targetParentMap: TargetParent
): K8sObject[] {
  function getChildren(group: K8sObject[] = []) {
    return group.flatMap((groupChild) => {
      const target = targetParentMap[groupChild.uid]
      return [groupChild, ...getChildren(target)]
    })
  }

  return getChildren([target])
}

export const unhealthyPod = (maybePod) =>
  maybePod?.kind === 'POD' && maybePod?.podPhase !== 'Running'

export function getParentListFromMap(
  targetParentMap: TargetParent,
  targets: K8sObject[]
) {
  // generate the list of valid objects first so it's not searched on each iteration of the loop below
  // this caused a performance issue, where a list of ~20k objects took almost a minute to process
  const objectIds = new Set(targets.map(({ uid }) => uid))

  return new Set(
    Object.keys(targetParentMap).filter((parent) => objectIds.has(parent))
  )
}

export function generateKubernetesSelectionOptions(targets: K8sObject[]) {
  const targetParentMap = groupKubernetesObjects(targets)
  const validParents = getParentListFromMap(targetParentMap, targets)

  const targetsByOwners: TargetsByOwner[] = targets
    .filter((t) => !t.ownerReferences || !validParents.has(t.ownerReferences))
    .map((t) => ({
      target: t,
      name: t.name || t.uid,
      uid: t.uid,
      children: getTargetAndDescendants(t, targetParentMap).reduce(
        (accum, child) => {
          if (
            // ignore the target itself
            child.uid === t.uid
          ) {
            return accum
          }

          if (!accum[child.kind]) {
            accum[child.kind] = []
          }

          ;(accum[child.kind] as K8sObject[]).push(child)
          return accum
        },
        {} as Record<string, K8sObject[]>
      )
    }))
  return targetsByOwners
}

export function getSelectedNamespace(
  selectedClusterId,
  k8sClients,
  forceSelectionLimit,
  selectedObjects
) {
  const isTargetSelected = (client) => {
    return (
      client.clientId === selectedClusterId ||
      client.assetId === selectedClusterId
    )
  }
  const selectedCluster = k8sClients.find((client) => isTargetSelected(client))

  if (Array.isArray(selectedObjects)) {
    // if selected objects is listed and they all have the same namespace use that one.
    if (new Set(selectedObjects.map((o) => o.namespace)).size === 1) {
      if (selectedObjects[0]?.clusterId === selectedCluster.clusterId) {
        return selectedObjects[0].namespace
      }
    }
  }

  const selectedClusterObjects = selectedCluster?.objects ?? []

  const numberOfObjects = selectedClusterObjects.length

  const namespaceSet = new Set(
    selectedClusterObjects.map((k8Object) => k8Object.namespace)
  )

  const tempSelectedNamespace =
    namespaceSet.size === 1 ? selectedClusterObjects[0].namespace : undefined

  if (
    tempSelectedNamespace &&
    selectedClusterObjects
      .filter((k8Object) => isTargetSelected(k8Object))
      .some((k8Object) => k8Object.namespace === tempSelectedNamespace)
  ) {
    return tempSelectedNamespace
  }

  return numberOfObjects > forceSelectionLimit
    ? Array.from(
        new Set(selectedClusterObjects.map((o) => o.namespace))
      ).sort()[0]
    : 'All'
}

export function getSelectedKubernetesTargets(
  targets,
  { selectedNamespace, selectedObjects }
) {
  return targets.filter((t) => {
    const isTargetSelected = selectedObjects.has(t.uid)

    if (isTargetSelected) {
      if (selectedNamespace && selectedNamespace.toLowerCase() !== 'all') {
        return t.namespace === selectedNamespace
      }
    }

    return isTargetSelected
  })
}

export function getK8sObjectsFromTargets(k8sObjects = [], clients = []) {
  const k8sTargetIds = new Set(k8sObjects.map((k8sObject) => k8sObject?.uid))
  const k8sTargetsByNamespace = k8sObjects
    .filter((o) => o?.namespace)
    .reduce((c, o) => {
      c[o?.namespace] = [...(c[o?.namespace] ?? []), o]
      return c
    }, {})
  return clients
    .map((client) =>
      client.objects.filter(
        (obj) =>
          k8sTargetIds.has(obj.uid) ||
          k8sTargetsByNamespace[obj.namespace]?.filter(
            (o) =>
              o.name === obj.name &&
              (!o.cluster_id || o.cluster_id === client.clusterId) &&
              (!o.clusterId || o.clusterId === client.clusterId)
          ).length > 0
      )
    )
    .reduce((objects, clientObjects) => [...objects, ...clientObjects], [])
}

/** EN-7101 Determine
 * - if pods are "top level" objects returned by a region or zone selector
 * - how many parents include these top level pods
 *
 * Note: There's probably a better way to store the initially `selectedObjects` data
 * returned by the API.
 * (Currently, we throw out most of the data and only keep a Set of string ids.)
 * Unfortunately, that would take a significant refactor, so this
 * function and it's "duck typing" will have to do for now.
 */
export function getSelectedPodsByOwners(
  selectedObjects: Set<string>,
  targetsByOwners: TargetsByOwner[],
  totalSelectedPods: number
) {
  /** Pods aren't top-level objects returned by a region or zone selector */
  if (selectedObjects.size !== totalSelectedPods) {
    return
  }

  /** Ensure the selected objects are pods */
  if (
    Array.from(selectedObjects).some((uid) =>
      targetsByOwners.some((owner) => owner.uid === uid)
    )
  ) {
    return
  }

  let podsCount = 0
  let affectedParentsCount = 0

  const stats = targetsByOwners.reduce((acc, curTarget) => {
    const pods = curTarget.children.POD ?? []
    const includedPods = pods.filter(
      (pod) => pod.uid && selectedObjects.has(pod.uid)
    )
    podsCount += includedPods.length

    if (includedPods.length > 0) {
      acc[curTarget.uid] = includedPods

      affectedParentsCount += 1
    }

    return acc
  }, {} as Record<string, K8sObject[]>)

  return {
    affectedParentsCount,
    podsCount, // this should equal totalSelectedPods
    stats
  }
}
